import React, { useEffect, useState, useCallback } from 'react';

const ProductShowcaseHero = () => {
  const [current, setCurrent] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  const products = [
    { key: 'mango', name: 'Fresh Mango Pulp', subline: '', pack: '/media/New-Images/mango-removebg-preview.png', bg: '#FFA62B', decor: '/media/mango_cut.png' },
    { key: 'pineapple', name: 'Fresh Pineapple Pulp', subline: '', pack: '/media/New-Images/Pineapple-removebg-preview.png', bg: '#F8C537', decor: '/media/pineapple_cut.png' },
    { key: 'guava', name: 'Fresh Guava Pulp', subline: '', pack: '/media/New-Images/Guava-removebg-preview.png', bg: '#FF8BA0', decor: '/media/guava_cut.png' },
    { key: 'passion', name: 'Fresh Passion Fruit Pulp', subline: '', pack: '/media/New-Images/Passion_fruit-removebg-preview.png', bg: '#7E57C2', decor: '/media/passion_cut.webp' },
    { key: 'jackfruit', name: 'Fresh Jackfruit Pulp', subline: '', pack: '/media/New-Images/Jack_fruit-removebg-preview.png', bg: '#FFB74D', decor: '/media/jack_cut.webp' },
    { key: 'chikoo', name: 'Fresh Chikoo Pulp', subline: '', pack: '/media/New-Images/Chikkoo-removebg-preview.png', bg: '#C47A4F', decor: '/media/chikoo_cut.webp' },
    { key: 'avocado', name: 'Fresh Avocado Pulp', subline: '', pack: '/media/New-Images/Avocado-removebg-preview.png', bg: '#5CB85C', decor: '/media/avacado_cut.webp' },
    { key: 'coconut', name: 'Fresh Tender Coconut Pulp', subline: '', pack: '/media/New-Images/Tender_coconut-removebg-preview.png', bg: '#8FD6C8', decor: '/media/coconut.webp' },
    { key: 'cocodate', name: 'Fresh Tender Coconut Date Pulp', subline: '', pack: '/media/New-Images/Tender_coconut_with_dates-removebg-preview.png', bg: '#B9855E', decor: '/media/coconut_dates_cut.webp' }
  ];

  const go = useCallback((dir = 'next') => {
    // fade out current
    setFadeIn(false);
    setTimeout(() => {
      setCurrent((i) => {
        if (dir === 'prev') return (i - 1 + products.length) % products.length;
        return (i + 1) % products.length;
      });
      // fade in new
      setTimeout(() => setFadeIn(true), 20);
    }, 220);
  }, [products.length]);

  useEffect(() => {
    const id = setInterval(() => go('next'), 7000);
    return () => clearInterval(id);
  }, [go]);

  const p = products[current];

  return (
    <section
      className="relative w-full min-h-[80vh] overflow-hidden pt-24 md:pt-28 transition-colors duration-500"
      style={{ background: p.bg }}
    >
      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-20 lg:py-24">
        {/* Bottom-left fruit cuts decorative image (per product, constrained to container) */}
        {p.decor && (
          <img
            src={p.decor}
            alt="Decorative fruit cuts"
            aria-hidden="true"
            className="hidden sm:block absolute left-0 bottom-[-40%] w-[45vw] max-w-[560px] pointer-events-none select-none opacity-100"
          />
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-center">
          {/* Left: Copy */}
          <div className={`text-white order-2 lg:order-1 text-center lg:text-left transition-all duration-500 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight drop-shadow-sm md:whitespace-nowrap">
              {p.name}
            </h1>
            <div className="mx-auto lg:mx-0 mt-5 h-1 w-32 md:w-40 bg-white/80 rounded"></div>
            <p className="mt-5 text-base sm:text-lg md:text-xl text-white/90 max-w-xl mx-auto lg:mx-0">
              {p.subline}
            </p>
            {/* Arrows hidden on mobile */}
            <div className="mt-8 hidden md:flex items-center gap-4">
              <button onClick={() => go('prev')} aria-label="Previous" className="w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center">
                <span className="sr-only">Previous</span>
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
              </button>
              <button onClick={() => go('next')} aria-label="Next" className="w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center">
                <span className="sr-only">Next</span>
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
              </button>
            </div>
          </div>

          {/* Right: Pack */}
          <div className={`relative flex justify-center lg:justify-end order-1 lg:order-2 transition-all duration-500 ${fadeIn ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-2 scale-95'}`}>
            <div className="w-[220px] xs:w-[260px] sm:w-[300px] md:w-[360px] lg:w-[440px] aspect-square">
              <img
                src={encodeURI(p.pack)}
                alt={p.name}
                className="w-full h-full object-contain drop-shadow-2xl md:rotate-[-8deg] transition-transform duration-700 ease-out float-slow float-wave"
              />
            </div>
          </div>
        </div>

        {/* Dots for mobile */}
        <div className="mt-6 flex md:hidden items-center justify-center gap-2">
          {products.map((_, i) => (
            <button key={i} onClick={() => { if (i !== current) { setCurrent(i); setFadeIn(true); } }} className={`${i===current ? 'w-6 bg-white' : 'w-2 bg-white/70'} h-2 rounded-full transition-all`} aria-label={`Go to slide ${i+1}`}></button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductShowcaseHero;
